import { KoobAlert, KoobInputSelectField } from '@koob/ui';
import { Container, FormLabel } from 'ui';
import { Stack } from '@koob/margaret';
import {
  TextField,
  SegmentedControlField,
  EditorField,
  AvatarField,
} from 'components/Fields';
import ExperienceOptionsSelectField from './ExperienceOptionsSelectField';
import React, { useEffect, useRef, useState } from 'react';
import TextFieldButton from 'components/Fields/TextFieldButton';
import { useTranslation } from 'react-i18next';
import { useField } from 'formik';
import LocalMultiSearchableSelectField from '../../../../components/Fields/LocalMultiSearchableSelectField';
import MapsPlaceSearchField from '../../../../components/MapsPlaceSearchField';
import KoobTitle from '../../../../components/Koob/KoobTitle';
import { initialLocale } from '../../../../utils';
import { useApp } from '../../../../hooks';
import SearchablePlaceField from '../../../../components/Fields/SearchablePlaceField';
import { getExperience, getExperiences } from 'api/node/experiences';
import { getHotels } from 'api/node/hotels';
import { getCountriesWithQuery } from 'api/node/countries';
import { getPartnerships } from 'api/node/partnerships';
import {
  PartnershipStatusEnum,
  ProgramAccommodationTypeEnum,
} from '@koob/enums';
import { PER_PAGE_FOR_LIST } from 'constants';

function SearchExperience({ currentLocale, index, isReadOnly }) {
  const { t } = useTranslation('experiences');
  const { locales } = useApp();

  const [translationsValue, , { setValue: setTranslations }] = useField(
    `programs[${index}].translations`,
  );
  const [experiencesValue] = useField(`programs[${index}].programExperiences`);

  const trans = translationsValue?.value;
  const exps = experiencesValue?.value;

  // Track which experiences have been appended by their ID to avoid duplicates
  const appendedIdsRef = useRef(new Set());
  const isInitialMount = useRef(true);

  useEffect(() => {
    // On initial mount, just record existing experience IDs without appending
    if (isInitialMount.current) {
      isInitialMount.current = false;
      appendedIdsRef.current = new Set(
        (exps ?? []).map((exp) => exp?.id ?? exp?.value),
      );
      return;
    }

    // Find new experiences that haven't been appended yet
    const newExps = (exps ?? []).filter(
      (exp) => !appendedIdsRef.current.has(exp?.id ?? exp?.value),
    );

    if (newExps.length > 0) {
      let translations = [];

      locales.forEach((locale) => {
        const existingTranslation =
          trans?.find((t) => t.locale === locale.value) || {};
        const defaultTranslation = trans?.find((t) => t.locale === 'en') || {};

        let programContent =
          existingTranslation?.program ?? defaultTranslation?.program ?? '';

        // Append all new experiences
        newExps.forEach((toAppend) => {
          const toAppendTranslation =
            toAppend?.translations?.find((t) => t.locale === locale.value) ??
            toAppend?.translations?.find((t) => t.locale === 'en') ??
            {};

          const expName = toAppendTranslation?.name ?? toAppend?.name;
          const expDesc =
            toAppendTranslation?.description ?? toAppend?.description;

          if (expName || expDesc) {
            programContent += '<br/>';
            if (expName) {
              programContent += `<h1><b>${expName}</b></h1>`;
            }
            if (expDesc) {
              programContent += `<p>${expDesc}</p>`;
            }
            programContent += '<br>';
          }
        });

        translations.push({
          locale: locale.value,
          title: existingTranslation?.title ?? defaultTranslation?.title,
          program: programContent,
        });
      });

      setTranslations(translations);

      // Mark new experiences as appended
      newExps.forEach((exp) => {
        appendedIdsRef.current.add(exp?.id ?? exp?.value);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exps]);

  return (
    <KoobInputSelectField
      multiple
      label={t('form.searchExperiences')}
      name={`programs[${index}].programExperiences`}
      query={getExperiences}
      lazy
      variables={{
        locale: currentLocale.value,
      }}
      renderOption={(option) => option.name}
      renderValue={(option) => option.name}
      disabled={isReadOnly}
    />
  );
}

const getPartnerInfo = (hotel, matchingPartnerships, currentOrgId) => {
  for (const partnership of matchingPartnerships) {
    const partnerId =
      partnership.firstPartner.id === currentOrgId
        ? partnership.secondPartner.id
        : partnership.firstPartner.id;
    const partnerName =
      partnership.firstPartner.id === currentOrgId
        ? partnership.secondPartner.name
        : partnership.firstPartner.name;

    if (parseInt(hotel.organizationId) === partnerId) {
      return { isPartnerHotel: true, partnerName };
    }
  }
  return { isPartnerHotel: false, partnerName: null };
};

export const DayForm = ({ index, currentLocale, isReadOnly = false }) => {
  const { t } = useTranslation('experiences');
  const { currentOrganization } = useApp();
  const currentOrgId = currentOrganization?.organizationId
    ? parseInt(currentOrganization.organizationId, 10)
    : null;
  const [accomodationType] = useField(`programs[${index}].accomodationType`);
  const [isIncludedAccomodation] = useField(
    `programs[${index}].isIncludedAccomodation`,
  );
  const [experiences] = useField(`programs[${index}].programExperiences`);
  const [attachment] = useField(`programs[${index}].picture`);
  const [program, , { setValue: setProgram }] = useField(
    `programs[${index}].program`,
  );
  const [suggestedExperience, , { setValue: setSuggestedExperience }] =
    useField(`programs[${index}].suggestedExperiences`);
  const [includedExperience] = useField(
    `programs[${index}].includedExperiences`,
  );

  const [{ value: programDatas }] = useField(`programs`);

  const suggestedIds = [];
  const incluedIds = [];

  programDatas?.forEach((item) => {
    item.suggestedExperiences?.forEach((exp) => {
      suggestedIds.push(exp.id);
    });
    item.includedExperiences?.forEach((exp) => {
      incluedIds.push(exp.id);
    });
  });

  const [{ value: bestMatches }, ,] = useField('bestMatches');

  const [{ value: incompatibilities }, ,] = useField('incompatibilities');

  const [{ value: options }, ,] = useField(`extras`);
  const optionsIds = options?.map((item) => item.id);

  const incompIds =
    incompatibilities?.length > 0
      ? incompatibilities?.map((item) => item.value)
      : [];

  const suggestedOptionIds = [
    ...(suggestedExperience.value?.map((option) => option.id) || []),
    ...incompIds,
    ...(optionsIds?.filter((item) => incluedIds.includes(item?.id)) ?? []),
  ];

  const includedOptionIds = [
    ...(includedExperience.value?.map((option) => option.id) || []),
    ...incompIds,
    ...(optionsIds?.filter((item) => suggestedIds.includes(item?.id)) ?? []),
  ];

  const [pois, , { setValue }] = useField(`programs[${index}].pois`);
  const [currentIndex, setCurrentIndex] = useState(index);
  const exp = experiences?.value;
  const expLength = exp?.length;
  const [oldLength, setOldLength] = useState(expLength);
  const [accomodationHotel] = useField(`programs[${index}].accomodationHotel`);
  const [alternativeHotels] = useField(`programs[${index}].alternativeHotels`);
  const [startPosition] = useField(`programs[${index}].startPosition`);
  const [endPosition] = useField(`programs[${index}].endPosition`);

  const [positionCountryIds, setPositionCountryIds] = useState([]);
  const [positionCountryNames, setPositionCountryNames] = useState([]);
  const [marketplacePartnerships, setMarketplacePartnerships] = useState([]);
  const [matchingPartnerships, setMatchingPartnerships] = useState([]);

  useEffect(() => {
    const fetchCountryIds = async () => {
      const countryNames = new Set();
      if (startPosition?.value?.country)
        countryNames.add(startPosition.value.country);
      if (endPosition?.value?.country)
        countryNames.add(endPosition.value.country);

      if (countryNames.size === 0) {
        setPositionCountryIds([]);
        setPositionCountryNames([]);
        return;
      }

      const ids = [];
      const names = [...countryNames];
      for (const name of countryNames) {
        try {
          const response = await getCountriesWithQuery({
            filters: { search: name },
          });
          const countries = response?.data ?? [];
          const match = countries.find((c) => c.title === name) || countries[0];
          if (match?.id) ids.push(match.id);
        } catch (e) {
          console.error('Failed to fetch country ID for:', name, e);
        }
      }
      setPositionCountryIds(ids);
      setPositionCountryNames(names);
    };

    fetchCountryIds();
  }, [startPosition?.value?.country, endPosition?.value?.country]);

  useEffect(() => {
    const fetchPartnerships = async () => {
      try {
        const response = await getPartnerships();
        const partnerships = response?.data ?? [];
        const filtered = partnerships.filter(
          (p) =>
            p.enableMarketplace && p.status === PartnershipStatusEnum.ENABLED,
        );
        setMarketplacePartnerships(filtered);
      } catch (e) {
        console.error('Failed to fetch partnerships:', e);
      }
    };

    fetchPartnerships();
  }, []);

  useEffect(() => {
    if (
      positionCountryIds.length === 0 ||
      marketplacePartnerships.length === 0
    ) {
      setMatchingPartnerships([]);
      return;
    }

    const matching = marketplacePartnerships.filter((partnership) => {
      // Determine the partner's ID (the one that's not the current org)
      const partnerId =
        partnership.firstPartner.id === currentOrgId
          ? partnership.secondPartner.id
          : partnership.firstPartner.id;

      // Check if any position country is covered by this partner
      return (partnership.countries ?? []).some((country) => {
        const countryIdStr = String(country.id);
        return (
          positionCountryIds.includes(countryIdStr) &&
          (country.coveredByPartnerIds ?? []).includes(partnerId)
        );
      });
    });

    setMatchingPartnerships(matching);
  }, [positionCountryIds, marketplacePartnerships, currentOrgId]);

  useEffect(() => {
    setCurrentIndex(index);
  }, [index]);

  useEffect(() => {
    if (expLength > oldLength || (!program?.value && expLength > 0)) {
      setProgram(
        `${program?.value ?? ''}<br/><h1><b>${
          exp[expLength - 1]?.name
        }</b></h1><p>${exp[expLength - 1]?.description ?? ''}</p><br>`,
      );
    }
    setOldLength(expLength);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expLength]);

  const handleSelectSuggestedOption = async (selectedOption) => {
    const experience = await getExperience({
      experienceId: selectedOption.id,
      locale: currentLocale.value ?? 'en',
    });

    const extras = experience?.data?.extras || [];

    if (extras.length > 0) {
      let current = suggestedExperience.value || [];
      if (!current.length || !current.find((e) => e.id === selectedOption.id)) {
        current = [
          ...current,
          {
            id: selectedOption.id,
            name: selectedOption.name,
          },
        ];
      }
      const mainSuggestedOption = current.find(
        (e) => e.id === selectedOption.id,
      );

      const extrasToAdd = extras.filter(
        (extra) => !includedOptionIds.includes(extra.id),
      );

      mainSuggestedOption.suggested = [...extrasToAdd];
      setSuggestedExperience(current);
    }
  };

  return currentIndex === index && index >= 0 ? (
    <Container size="full">
      <Stack direction="column" gutterSize={1.5} size="full">
        <Stack size="full">
          <TextField
            required
            label={t('form.title')}
            content=""
            name={`programs[${index}].title`}
            disabled={isReadOnly}
          />
        </Stack>

        <Stack gutterSize={1.5} size="full">
          <SearchablePlaceField
            label={t('form.startLocation')}
            name={`programs[${index}].startPosition`}
            required
            block
            disabled={isReadOnly}
          />

          <Stack
            gutterSize={1}
            style={{
              flexWrap: 'wrap',
              alignItems: 'stretch',
            }}
          >
            <TextFieldButton
              type="number"
              step="1"
              label={t('form.startingTimeHour')}
              name={`programs[${index}].startingTimeHour`}
              content={'H'}
              min="0"
              max="23"
              disabled={isReadOnly}
            />
            <TextFieldButton
              type="number"
              step="15"
              label={t('form.startingTimeMin')}
              name={`programs[${index}].startingTimeMin`}
              content={'min'}
              min="0"
              max="59"
              disabled={isReadOnly}
            />
          </Stack>
        </Stack>

        <Stack gutterSize={1.5} size="full">
          <SearchablePlaceField
            label={t('form.endLocation')}
            name={`programs[${index}].endPosition`}
            required
            block
            disabled={isReadOnly}
          />

          <Stack
            gutterSize={1}
            style={{
              flexWrap: 'wrap',
              alignItems: 'stretch',
            }}
          >
            <TextFieldButton
              type="number"
              step="1"
              label={t('form.endingTimeHour')}
              name={`programs[${index}].endingTimeHour`}
              content={'H'}
              min="0"
              max="23"
              disabled={isReadOnly}
            />

            <TextFieldButton
              type="number"
              step="15"
              label={t('form.endingTimeMin')}
              name={`programs[${index}].endingTimeMin`}
              content={'min'}
              min="0"
              max="59"
              disabled={isReadOnly}
            />
          </Stack>
        </Stack>

        {matchingPartnerships.length > 0 && (
          <KoobAlert variant="info" block>
            {t('form.partnerOfferingsAlertPrefix')}{' '}
            <strong>
              {matchingPartnerships
                .map((p) =>
                  p.firstPartner.id === currentOrgId
                    ? p.secondPartner.name
                    : p.firstPartner.name,
                )
                .join(` ${t('misc:and')} `)}
            </strong>{' '}
            {t('form.partnerOfferingsAlertSuffix')}
          </KoobAlert>
        )}

        <div className="w-full relative">
          {currentLocale.value !== initialLocale.value && (
            <div className="absolute z-10 h-full w-full flex justify-center items-center">
              <div className="text-orange-500 px-3 py-2 bg-white border border-orange-500 rounded">
                {t('program.experiences.selectEnglish')}
              </div>
            </div>
          )}
          <div
            className={
              currentLocale.value !== initialLocale.value &&
              'pointer-events-none opacity-50'
            }
          >
            <div className="w-full border rounded shadow p-5">
              <FormLabel>{t('form.POIs')}</FormLabel>
              <div className="my-2 flex flex-col space-y-2">
                {pois.value?.map((poi, index) => (
                  <div
                    className="flex justify-between items-center"
                    key={index}
                  >
                    <div>{poi.name}</div>

                    <button
                      type="button"
                      className="border-none text-red-500"
                      disabled={isReadOnly}
                      onClick={() => {
                        setValue(pois.value.filter((_, i) => i !== index));
                      }}
                    >
                      <svg className="h-4 w-4" viewBox="0 0 384 512">
                        <path d="M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
              {!isReadOnly && (
                <MapsPlaceSearchField
                  name={`programs[${index}].pois`}
                  multiple
                />
              )}
            </div>
          </div>
        </div>

        <div className="w-full relative">
          {currentLocale.value !== initialLocale.value && (
            <div className="absolute z-10 h-full w-full flex justify-center items-center">
              <div className="text-orange-500 px-3 py-2 bg-white border border-orange-500 rounded">
                {t('program.experiences.selectEnglish')}
              </div>
            </div>
          )}
          <div
            className={
              currentLocale.value !== initialLocale.value &&
              'pointer-events-none opacity-50'
            }
          >
            <SearchExperience
              index={index}
              currentLocale={currentLocale}
              isReadOnly={isReadOnly}
            />
          </div>
        </div>

        <div className="w-full">
          <EditorField
            name={`programs[${index}].program`}
            label={t('form.program')}
            required
            disabled={isReadOnly}
          />
        </div>

        <LocalMultiSearchableSelectField
          label={t('form.meals')}
          name={`programs[${index}].meals`}
          options={[
            { label: 'Breakfast', value: 'breakfast' },
            { label: 'Lunch', value: 'lunch' },
            { label: 'Dinner', value: 'dinner' },
          ]}
          renderOption={(option) => option.label}
          renderSelectedOption={(option) => option.label}
          disabled={isReadOnly}
        />

        <Stack size="full">
          <ExperienceOptionsSelectField
            label={t('form.includedOptions')}
            name={`programs[${index}].includedExperiences`}
            query={getExperiences}
            variables={{
              types: ['Activity', 'Transfer', 'Extra'],
              excludedExperiences: suggestedOptionIds,
              locale: currentLocale.value,
              asOptions: true,
            }}
            bestMatches={bestMatches}
            clearFieldName="extras"
            data-testid="primary-included-options"
          />
        </Stack>

        <Stack size="full">
          <ExperienceOptionsSelectField
            label={t('form.suggestedOptions')}
            name={`programs[${index}].suggestedExperiences`}
            query={getExperiences}
            variables={{
              types: ['Activity', 'Transfer', 'Extra'],
              excludedExperiences: includedOptionIds,
              locale: currentLocale.value,
              asOptions: true,
            }}
            bestMatches={bestMatches}
            clearFieldName="extras"
            data-testid="primary-suggested-options"
            onSelectOption={(opt) => {
              handleSelectSuggestedOption(opt);
            }}
          />
        </Stack>
        <Stack size={'full'}>
          <section className="grid grid-cols-1 divide-y w-full">
            {suggestedExperience?.value?.map((exp, seIndex) => {
              const requiredOptionsSelected =
                suggestedExperience.value?.[seIndex]?.required || [];
              const suggestedOptionsSelected =
                suggestedExperience.value?.[seIndex]?.suggested || [];

              return (
                <details className="group py-1 text-sm w-full" key={seIndex}>
                  <summary className="flex cursor-pointer flex-row items-center justify-between w-full font-semibold text-gray-800 marker:[font-size:0px]">
                    {exp.name} additional service
                    <svg
                      className="h-6 w-6 rotate-0 transform text-gray-400 group-open:rotate-180"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </summary>

                  <div className={'flex flex-col gap-4'}>
                    <ExperienceOptionsSelectField
                      label={t('steps.options.requiredOptions')}
                      name={`programs[${index}].suggestedExperiences[${seIndex}].required`}
                      query={getExperiences}
                      variables={{
                        types: ['Activity', 'Transfer', 'Extra'],
                        excludedExperiences: [
                          ...includedOptionIds,
                          exp.id,
                          ...suggestedOptionsSelected.map(
                            (option) => option.id,
                          ),
                        ],
                        locale: currentLocale.value,
                        asOptions: true,
                      }}
                      bestMatches={bestMatches}
                      clearFieldName="extras"
                      data-testid="secondary-included-options"
                    />

                    <ExperienceOptionsSelectField
                      label={t('steps.options.suggestedOptions')}
                      name={`programs[${index}].suggestedExperiences[${seIndex}].suggested`}
                      query={getExperiences}
                      variables={{
                        types: ['Activity', 'Transfer', 'Extra'],
                        excludedExperiences: [
                          ...includedOptionIds,
                          exp.id,
                          ...requiredOptionsSelected.map((option) => option.id),
                        ],
                        locale: currentLocale.value,
                        asOptions: true,
                      }}
                      bestMatches={bestMatches}
                      clearFieldName="extras"
                      data-testid="secondary-suggested-options"
                    />
                  </div>
                </details>
              );
            })}
          </section>
        </Stack>

        <div className="my-2 w-full flex flex-col space-y-5">
          <SegmentedControlField
            required
            name={`programs[${index}].accomodationType`}
            options={[
              { label: t('form.hotel'), value: 'hotel' },
              { label: t('form.nightOnBoard'), value: 'nightTransfer' },
              { label: t('form.noAccomodation'), value: 'noAccomodation' },
              { label: t('form.freeAccomodation'), value: 'freeAccomodation' },
            ]}
            label={t('form.accomodationType')}
          />

          {accomodationType?.value === ProgramAccommodationTypeEnum.HOTEL &&
            positionCountryNames.length > 0 && (
              <KoobAlert variant="info" block>
                {t('form.hotelsFromCountryAlertPrefix')}{' '}
                <strong>
                  {positionCountryNames.join(` ${t('misc:and')} `)}
                </strong>{' '}
                {t('form.hotelsFromCountryAlertSuffix')}
              </KoobAlert>
            )}

          {accomodationType?.value === ProgramAccommodationTypeEnum.HOTEL && (
            <>
              <div className="mt-5">
                <SegmentedControlField
                  label={t('form.includedAccomodation')}
                  name={`programs[${index}].isIncludedAccomodation`}
                  options={[
                    { label: t('form.no'), value: false },
                    { label: t('form.yes'), value: true },
                  ]}
                />
              </div>

              <KoobInputSelectField
                label={t('form.hotel')}
                name={`programs[${index}].accomodationHotel`}
                query={getHotels}
                variables={{
                  locale: currentLocale.value,
                  excludedIds: (alternativeHotels?.value || []).map(
                    (h) => h.id,
                  ),
                  countries:
                    positionCountryIds.length > 0
                      ? positionCountryIds
                      : undefined,
                  includePartnerHotels: matchingPartnerships.length > 0,
                }}
                renderOption={(option) => {
                  const { isPartnerHotel, partnerName } = getPartnerInfo(
                    option,
                    matchingPartnerships,
                    currentOrgId,
                  );
                  return (
                    <div className="flex items-center gap-2">
                      <span>{option.displayName}</span>
                      {option.alpha2 && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          {option.alpha2}
                        </span>
                      )}
                      {isPartnerHotel && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700">
                          <i className="fas fa-handshake text-xs" />
                          {partnerName}
                        </span>
                      )}
                    </div>
                  );
                }}
                renderValue={(option) => option.displayName}
                required
              />

              <KoobInputSelectField
                multiple
                label={
                  isIncludedAccomodation?.value === true
                    ? t('form.alternativeHotels')
                    : t('form.suggestedHotels')
                }
                name={`programs[${index}].alternativeHotels`}
                query={getHotels}
                variables={{
                  locale: currentLocale.value,
                  excludedIds: accomodationHotel?.value?.id
                    ? [accomodationHotel.value.id]
                    : [],
                  countries:
                    positionCountryIds.length > 0
                      ? positionCountryIds
                      : undefined,
                  includePartnerHotels: matchingPartnerships.length > 0,
                }}
                renderOption={(option) => option.displayName}
                renderOptionContent={(option) => {
                  const { isPartnerHotel, partnerName } = getPartnerInfo(
                    option,
                    matchingPartnerships,
                    currentOrgId,
                  );
                  return (
                    <div className="flex items-center gap-2">
                      <span>{option.displayName}</span>
                      {option.alpha2 && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          {option.alpha2}
                        </span>
                      )}
                      {isPartnerHotel && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700">
                          <i className="fas fa-handshake text-xs" />
                          {partnerName}
                        </span>
                      )}
                    </div>
                  );
                }}
                renderValue={(option) => option.displayName}
              />
            </>
          )}

          {accomodationType?.value ===
            ProgramAccommodationTypeEnum.NIGHT_TRANSFER && (
            <KoobInputSelectField
              label={t('form.nightOnBoard')}
              name={`programs[${index}].accomodationExperience`}
              query={getExperiences}
              variables={{
                types: ['Transfer'],
                time: 'night',
                locale: currentLocale.value,
                page: 1,
                perPage: PER_PAGE_FOR_LIST,
              }}
              renderOption={(option) => option.name}
              renderValue={(option) => option.name}
              data-testid="night-transfer"
              required
            />
          )}
        </div>
      </Stack>
      <div className="mt-4">
        <AvatarField
          label={t('form.program_picture')}
          name={`programs[${index}].picture`}
          shape="square"
          url={
            typeof attachment?.value === 'string'
              ? attachment.value
              : attachment?.value?.imageUrl || attachment?.value?.url
          }
          forImage
          disabled={isReadOnly}
        />
      </div>
    </Container>
  ) : (
    <div className="h-32 flex justify-center items-center">
      <div className="text-center">
        <svg className="mb-1 h-12 w-12 mx-auto" viewBox="0 0 512 512">
          <path d="M144 0c8.8 0 16 7.2 16 16V64H352V16c0-8.8 7.2-16 16-16s16 7.2 16 16V64h32c35.3 0 64 28.7 64 64v32 32V448c0 35.3-28.7 64-64 64H96c-35.3 0-64-28.7-64-64V192 160 128c0-35.3 28.7-64 64-64h32V16c0-8.8 7.2-16 16-16zM448 192H64V448c0 17.7 14.3 32 32 32H416c17.7 0 32-14.3 32-32V192zM416 96H96c-17.7 0-32 14.3-32 32v32H448V128c0-17.7-14.3-32-32-32zM352 336c0 8.8-7.2 16-16 16H272v64c0 8.8-7.2 16-16 16s-16-7.2-16-16V352H176c-8.8 0-16-7.2-16-16s7.2-16 16-16h64V256c0-8.8 7.2-16 16-16s16 7.2 16 16v64h64c8.8 0 16 7.2 16 16z" />
        </svg>
        <KoobTitle size="text-xl">{t('form.addProgramDay')}</KoobTitle>
      </div>
    </div>
  );
};
